/**
 * One listing, in full - the screen a reviewer actually decides on.
 *
 * The point of this screen is that a listing can have THREE versions at once, and
 * they mean different things:
 *
 *   published   what members see right now
 *   pending     an edit (or a first submission) awaiting a decision
 *   draft       the owner's work in progress, not submitted
 *
 * So the versions are shown side by side rather than merged: approving a pending
 * version replaces the published one, and an admin reading only the merged result
 * could not tell what they were about to change. `reviews` is the history of
 * decisions on this listing, which is where the previous reviewer's remarks live.
 *
 * The lifecycle buttons are the same set the queue offers, and the same component
 * builds them, so the two screens cannot drift.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardHeader,
  Definition,
  DefinitionList,
  ErrorNotice,
  Field,
  Input,
  LoadingRows,
  Page,
  Select,
  SuccessNotice,
  Textarea,
  statusTone,
} from "../components/primitives";
import { ListingActions } from "../components/ListingActions";
import {
  fetchListing,
  updateListing,
  type AdminVersion,
  type ListingAdmin,
  type ListingAdminUpdate,
} from "../lib/api/familybiz";
// The category picker hits the same `/admin/categories` route spec 09 mounted, so
// it reuses that module's typed function rather than declaring a second one.
import { fetchCategories } from "../lib/api/vendors";
import type { DiscountType } from "../lib/api/types";
import { useMutation, useQuery } from "../lib/hooks";
import { formatDate, formatDateTime, humanizeStatus } from "../lib/format";

const DISCOUNT_TYPES: DiscountType[] = ["percentage", "amount", "other"];

export function FamilyBizDetailScreen() {
  const { listingId = "" } = useParams();
  const [saved, setSaved] = useState<string | null>(null);

  const query = useQuery<{ listing: ListingAdmin; message: string }>(
    () => fetchListing(listingId),
    [listingId],
  );
  const { data, error, loading, reload } = query;

  if (error) {
    return (
      <Page>
        <ErrorNotice error={error} onRetry={reload} />
        <Link to="/family-biz" className="btn btn--ghost" style={{ alignSelf: "flex-start" }}>
          Back to FamilyBiz
        </Link>
      </Page>
    );
  }

  if (loading && !data) {
    return (
      <Page>
        <Card flush>
          <LoadingRows rows={5} columns={3} />
        </Card>
      </Page>
    );
  }

  if (!data) return null;

  const listing = data.listing;

  return (
    <Page>
      {saved ? <SuccessNotice message={saved} onDismiss={() => setSaved(null)} /> : null}

      <Card>
        <CardHeader
          title={
            <span className="row row--tight">
              {(listing.published ?? listing.pending ?? listing.draft)?.business_name ??
                "Unnamed listing"}
              <Badge tone={statusTone(listing.status)}>{humanizeStatus(listing.status)}</Badge>
              {listing.requested_action ? (
                <Badge tone="pending">asks to {listing.requested_action}</Badge>
              ) : null}
            </span>
          }
          hint={
            listing.submitted_at
              ? `Submitted ${formatDateTime(listing.submitted_at)}`
              : "Never submitted"
          }
          actions={
            <span className="row row--tight">
              <ListingActions
                listing={listing}
                showOpen={false}
                onChanged={(message) => {
                  setSaved(message);
                  reload();
                }}
              />
              <Link to="/family-biz?queue=true" className="btn btn--ghost btn--sm">
                Back to the queue
              </Link>
            </span>
          }
        />

        <DefinitionList>
          <Definition term="Owner">
            {listing.owner_name ? (
              <>
                {listing.owner_name}
                {listing.owner_mobile_number ? ` · ${listing.owner_mobile_number}` : ""}
                {listing.owner_email ? ` · ${listing.owner_email}` : ""}
              </>
            ) : (
              "Removed member"
            )}
          </Definition>
          <Definition term="Live version">
            {listing.published
              ? `v${listing.published.version_no}, approved (${formatDate(listing.decided_at)})`
              : "Not approved yet"}
          </Definition>
          <Definition term="Awaiting review">
            {listing.pending ? `v${listing.pending.version_no}, submitted` : "Nothing pending"}
          </Definition>
          <Definition term="Draft">
            {listing.draft ? `v${listing.draft.version_no}, not submitted` : "No working draft"}
          </Definition>
          <Definition term="Display order">{listing.display_order}</Definition>
          <Definition term="Latest note">{listing.latest_remarks ?? "-"}</Definition>
        </DefinitionList>
      </Card>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
          gap: 24,
          alignItems: "start",
        }}
      >
        <VersionCard title="Published - what members see" version={listing.published} />
        <VersionCard title="Pending - awaiting your decision" version={listing.pending} />
        <VersionCard title="Draft - owner's work in progress" version={listing.draft} />
      </div>

      <Card>
        <CardHeader
          title="Review history"
          hint="Every decision on this listing, newest first as recorded."
        />
        {listing.reviews.length === 0 ? (
          <p className="text-meta text-small" style={{ margin: 0 }}>
            No decisions have been recorded on this listing yet.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Decision</th>
                  <th scope="col">Note</th>
                  <th scope="col">When</th>
                </tr>
              </thead>
              <tbody>
                {listing.reviews.map((review) => (
                  <tr key={`${review.decision}-${review.created_at}`}>
                    <td>
                      <Badge tone={review.decision === "approved" ? "active" : review.decision === "rejected" ? "danger" : "pending"}>
                        {review.decision.replace(/_/g, " ")}
                      </Badge>
                    </td>
                    <td>{review.remarks ?? <span className="text-meta">-</span>}</td>
                    <td>{formatDateTime(review.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AdminEditCard
        listing={listing}
        onSaved={(message) => {
          setSaved(message);
          reload();
        }}
      />
    </Page>
  );
}

/** One version, rendered whole. Absent versions say so rather than showing blanks
 *  that read as empty fields. */
function VersionCard({ title, version }: { title: string; version: AdminVersion | null }) {
  return (
    <Card>
      <CardHeader
        title={title}
        hint={version ? `Version ${version.version_no} · ${version.state}` : undefined}
      />
      {!version ? (
        <p className="text-meta text-small" style={{ margin: 0 }}>
          No such version.
        </p>
      ) : (
        <>
          {version.logo_url ? (
            <img
              src={version.logo_url}
              alt=""
              style={{
                maxWidth: 92,
                borderRadius: 12,
                border: "1px solid var(--border-warm)",
                marginBottom: 12,
              }}
            />
          ) : null}
          {version.photos.length > 0 ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              {version.photos.map((photo) => (
                <a key={photo.slot} href={photo.url} target="_blank" rel="noreferrer">
                  <img
                    src={photo.url}
                    alt={`Photo ${photo.slot + 1}`}
                    style={{
                      width: 120,
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      borderRadius: 12,
                      border: "1px solid var(--border-warm)",
                    }}
                  />
                </a>
              ))}
            </div>
          ) : null}
          <DefinitionList>
            <Definition term="Name">{version.business_name}</Definition>
            <Definition term="Category">{version.category_name ?? "Uncategorised"}</Definition>
            <Definition term="Offer">
              {version.discount_label ?? "No discount"}
              {version.terms_and_conditions ? ` - ${version.terms_and_conditions}` : ""}
            </Definition>
            <Definition term="Description">{version.description ?? "-"}</Definition>
            <Definition term="Phone">{version.phone ?? "-"}</Definition>
            <Definition term="Email">{version.email ?? "-"}</Definition>
            <Definition term="Website">{version.website_url ?? "-"}</Definition>
            <Definition term="Address">{version.address ?? "-"}</Definition>
            <Definition term="Map">{version.map_url ?? "-"}</Definition>
            <Definition term="Owner shown">
              {version.show_owner_name ? "Yes, with the listing" : "No, kept private"}
            </Definition>
            <Definition term="Terms">{version.terms_and_conditions ?? "-"}</Definition>
            <Definition term="Links">
              {version.links.length === 0 ? (
                "-"
              ) : (
                <ul className="bullets" style={{ listStyle: "none", paddingLeft: 0 }}>
                  {version.links.map((link) => (
                    <li key={`${link.platform}-${link.url}`}>
                      {link.platform}: {link.url}
                    </li>
                  ))}
                </ul>
              )}
            </Definition>
          </DefinitionList>
        </>
      )}
    </Card>
  );
}

/**
 * FR-7.2.3 - an admin editing a listing's content directly.
 *
 * This writes a new version attributed as `edited_by_admin`, and is audited. It is
 * the escape hatch for a typo the owner cannot fix themselves (they may be locked
 * out of editing while a submission is under review), NOT a way to rewrite a
 * member's listing: the review history shows that an admin changed it, and the
 * member is told.
 */
function AdminEditCard({
  listing,
  onSaved,
}: {
  listing: ListingAdmin;
  onSaved: (message: string) => void;
}) {
  // Prefilled from the pending version when there is one - that is the thing being
  // decided on - and otherwise from what members currently see.
  const source = listing.pending ?? listing.published ?? listing.draft;

  const [form, setForm] = useState({
    business_name: source?.business_name ?? "",
    category_id: source?.category_id ?? "",
    description: source?.description ?? "",
    phone: source?.phone ?? "",
    email: source?.email ?? "",
    website_url: source?.website_url ?? "",
    address: source?.address ?? "",
    map_url: source?.map_url ?? "",
    discount_type: (source?.discount_type ?? "") as DiscountType | "",
    discount_value: source?.discount_value ?? "",
    terms_and_conditions: source?.terms_and_conditions ?? "",
    show_owner_name: source?.show_owner_name ?? false,
    display_order: String(listing.display_order ?? 0),
  });

  const categories = useQuery(() => fetchCategories(), []);

  const mutation = useMutation(
    () => {
      const payload: ListingAdminUpdate = { business_name: form.business_name.trim() };
      if (form.category_id) payload.category_id = form.category_id;
      payload.description = form.description.trim();
      payload.phone = form.phone.trim();
      payload.email = form.email.trim();
      payload.website_url = form.website_url.trim();
      payload.address = form.address.trim();
      payload.map_url = form.map_url.trim();
      if (form.discount_type) payload.discount_type = form.discount_type;
      payload.discount_value = form.discount_value.trim();
      payload.terms_and_conditions = form.terms_and_conditions.trim();
      payload.show_owner_name = form.show_owner_name;
      payload.display_order = Number(form.display_order) || 0;
      return updateListing(listing.id, payload);
    },
    { onSuccess: (result) => onSaved(result?.message ?? "Listing updated.") },
  );

  return (
    <Card>
      <CardHeader
        title="Edit listing content"
        hint="Recorded as an admin edit in the review history and the audit log. The member is told."
      />
      <div className="form-grid">
        <Field label="Business name">
          {(id) => (
            <Input
              id={id}
              value={form.business_name}
              onChange={(event) => setForm({ ...form, business_name: event.target.value })}
              maxLength={160}
            />
          )}
        </Field>
        <Field label="Category">
          {(id) => (
            <Select
              id={id}
              value={form.category_id}
              onChange={(event) => setForm({ ...form, category_id: event.target.value })}
            >
              <option value="">Uncategorised</option>
              {(categories.data?.items ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Discount type">
          {(id) => (
            <Select
              id={id}
              value={form.discount_type}
              onChange={(event) => setForm({ ...form, discount_type: event.target.value as DiscountType | "" })}
            >
              <option value="">No discount</option>
              {DISCOUNT_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Discount value">
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              value={form.discount_value}
              onChange={(event) => setForm({ ...form, discount_value: event.target.value })}
            />
          )}
        </Field>
        <Field label="Phone">
          {(id) => (
            <Input id={id} value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          )}
        </Field>
        <Field label="Email">
          {(id) => (
            <Input
              id={id}
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          )}
        </Field>
        <Field label="Website">
          {(id) => (
            <Input
              id={id}
              value={form.website_url}
              onChange={(event) => setForm({ ...form, website_url: event.target.value })}
            />
          )}
        </Field>
        <Field label="Map link">
          {(id) => (
            <Input
              id={id}
              value={form.map_url}
              onChange={(event) => setForm({ ...form, map_url: event.target.value })}
            />
          )}
        </Field>
        <Field label="Display order">
          {(id) => (
            <Input
              id={id}
              type="number"
              value={form.display_order}
              onChange={(event) => setForm({ ...form, display_order: event.target.value })}
            />
          )}
        </Field>
        <div className="form-grid__wide">
          <Field label="Address">
            {(id) => (
              <Input id={id} value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
            )}
          </Field>
        </div>
        <div className="form-grid__wide">
          <Field label="Description">
            {(id) => (
              <Textarea
                id={id}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            )}
          </Field>
        </div>
        <div className="form-grid__wide">
          <Field label="Terms and conditions">
            {(id) => (
              <Textarea
                id={id}
                value={form.terms_and_conditions}
                onChange={(event) => setForm({ ...form, terms_and_conditions: event.target.value })}
              />
            )}
          </Field>
        </div>
        <label className="row row--tight" style={{ gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={form.show_owner_name}
            onChange={(event) => setForm({ ...form, show_owner_name: event.target.checked })}
          />
          <span className="text-small">Show the owner's name with the listing</span>
        </label>
      </div>

      <div style={{ marginTop: 16 }}>
        <ErrorNotice error={mutation.error} />
      </div>

      <div className="row row--end" style={{ marginTop: 16 }}>
        <Button
          variant="accent"
          disabled={!form.business_name.trim() || mutation.pending}
          onClick={() => void mutation.run()}
        >
          {mutation.pending ? "Saving…" : "Save admin edit"}
        </Button>
      </div>
    </Card>
  );
}
